import { useSelector } from "react-redux";
import {
  selectCurrentSavedSearchId,
  selectPrivateSearchList,
  selectPublicSearchList,
} from "../../../store/searchStore/searchSelectors";
import PublicIcon from '@material-ui/icons/Public';
import PermIdentityIcon from '@material-ui/icons/PermIdentity';
import CollapsibleMenuList from "../../../components/CollapsibleMenuList";
import { CollapsableListItemProps } from "../../../components/CollapsableListItem/CollapsableListItem";
import useNavigator from "../../../hooks/navigator.hook";
import ColumnsSelector from "../../../components/ColumnsSelector";
import ReportingDialog from "../../../components/ReportingDialog";
import { IconButton } from "@material-ui/core";
import DeleteIcon from '@material-ui/icons/Delete';
import { ModalType } from "../../../app/shared/modal-context/constants";
import { useShowModal } from "../../../app/shared/modal-context/modal.context";
import { useTypedDispatch } from "../../../app/state";
import { deleteSavedSearch } from "../../../store/searchStore/searchRequests";
import { useSnackbar } from "../../../app/shared/snackbar-context/snackbar.context";
import { PERMISSIONS } from "../../../store/userStore/permissions.enum";
import { CheckPermission } from "../../../requests/CheckPermission";

import classes from './index.module.scss';

const SavedSearchList = () => {
  const dispatch = useTypedDispatch();
  const { navigateToSearchPage } = useNavigator();
  const { showModal } = useShowModal();
  const { openSnackbar } = useSnackbar();
  const publicSearchList = useSelector(selectPublicSearchList);
  const privateSavedSearchList = useSelector(selectPrivateSearchList);
  const currentSearchId = useSelector(selectCurrentSavedSearchId);

  const onSavedSearchClick = (searchId: number) => {
    const publicSearch = publicSearchList.find((search) => search.id === searchId);

    if (!publicSearch) {
      return;
    }

    const data = JSON.parse(publicSearch.savedData);
    const { searchParams, columnList, reportSettings } = data;

    ColumnsSelector.saveColumnsArray(columnList);
    ReportingDialog.saveReportValues(reportSettings);
    navigateToSearchPage({ ...searchParams, savedSearchId: searchId });
  }

  const onContinueDeleteSavedSearch = (id: number) => {
    dispatch(deleteSavedSearch(id))
      .then(() => {
        openSnackbar({
          title: 'Operation was successful',
          message: "Saved search has been deleted successfully",
          severity: 'success',
        });
      })
  }

  const onDeleteClick = (id: number) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const publicSearch = publicSearchList.find((search) => search.id === id);
    const name = publicSearch?.name;

    showModal(ModalType.MODAL_GENERIC_CONFIRM, {
      title: "Delete Saved Search",
      text: `You want to delete saved search (${name}). Proceed?`,
      confirmButtonHandler: () => onContinueDeleteSavedSearch(id),
      confirmButtonText: "Delete",
    });
  }

  const groups: CollapsableListItemProps[] = [];
  publicSearchList.length > 0 && groups.push({
    icon: <PublicIcon />,
    listTitle: 'Public',
    items: publicSearchList.map((search) => {
      return {
        secondary: search.name,
        id: search.id,
        rightIcon: (
          <CheckPermission permissionName={PERMISSIONS.PUBLIC_SAVED_SEARCH_ACTIONS}>
            <IconButton
              size="small"
              color="default"
              onClick={onDeleteClick(search.id)}
              className={classes.deleteButton}
            >
              <DeleteIcon />
            </IconButton>
          </CheckPermission>
        ),
      }
    })
  } as CollapsableListItemProps);

  privateSavedSearchList.length > 0 && groups.push({
    icon: <PermIdentityIcon />,
    listTitle: 'Private',
    items: privateSavedSearchList.map((search) => {
      return {
        secondary: search.name,
        id: search.id,
        rightIcon: (
          <CheckPermission permissionName={PERMISSIONS.PUBLIC_SAVED_SEARCH_ACTIONS}>
            <IconButton
              size="small"
              color="default"
              onClick={onDeleteClick(search.id)}
              className={classes.deleteButton}
            >
              <DeleteIcon />
            </IconButton>
          </CheckPermission>
        ),
      }
    })
  } as CollapsableListItemProps);


  return (
    <CollapsibleMenuList
      isOpened
      selectItem={onSavedSearchClick}
      selectedItemId={currentSearchId}
      groups={groups}
      classes={
        {
          menuItem: classes.listItem
        }
      }
    />
  );
}

export default SavedSearchList;